import * as React from 'react';
import { Form } from 'react-bootstrap';
import { Dropdown, useCacheQuery } from 'gossamer-front-end';
import { Specialty } from 'cb-model';
import {
  EmploymentGradeOptions,
  SpecialtyGroupOptions,
  VisibilityLimitOptions,
  YearGroupOptions,
} from 'cb-model/SharedOptions';
import { EventDetailsSectionLayout } from './EventDetailsSectionLayout';
import { EventDetails, EventErrors, HandleEventDetailsChange, ValidateEventFormField } from './eventDetailsFormTypes';

type Props<T extends EventDetails> = {
  eventDetails: T | undefined;
  handleDetailsChange: HandleEventDetailsChange<T>;
  errors: EventErrors;
  setErrors: React.Dispatch<React.SetStateAction<EventErrors>>;
  validateForm: ValidateEventFormField<T>;
};

const getVisibilityLimitLabel = (visibilityLimit: string | null | undefined): string => {
  if (!visibilityLimit) return 'Not set';
  const option = VisibilityLimitOptions.find((o) => o.id === visibilityLimit);
  return option ? option.label : visibilityLimit;
};

export function EventAudienceDetailsSection<T extends EventDetails>(props: Props<T>): React.JSX.Element {
  const { eventDetails, handleDetailsChange } = props;

  const specialtyGroup = eventDetails?.specialty_group as string | null | undefined;

  const specialtiesQuery = useCacheQuery(
    Specialty,
    {
      where: {
        and: specialtyGroup ? [{ fieldId: 'specialty_group_id', operator: 'eq', value: specialtyGroup }] : [],
      },
      order: [['label', 'ASC']],
      limit: 500,
    },
    !specialtyGroup,
  );

  const specialtyOptions = React.useMemo(
    () => (specialtiesQuery.data || []).map((s: any) => ({ id: String(s.id), label: String(s.label) })),
    [specialtiesQuery.data],
  );

  return (
    <EventDetailsSectionLayout
      title="Audience details"
      subtitle="Define which groups of people your event is intended for. This will ensure your event shows up in searches using these criteria."
    >
      <Form.Group>
        <Form.Label>Who can see the event and book it?</Form.Label>
        <div>
          <b>{getVisibilityLimitLabel(eventDetails?.visibility_limit)}</b>
        </div>
        <Form.Text muted>This is set by the Event Profile selected above.</Form.Text>
      </Form.Group>

      <Form.Group controlId="specialty-group">
        <Form.Label>Specialty group</Form.Label>
        <Dropdown
          mandatory={false}
          onItemSelected={(value) => {
            handleDetailsChange('specialty_group' as keyof T, value || null);
            // Clear specialty when group changes
            handleDetailsChange('specialties' as keyof T, null);
          }}
          options={SpecialtyGroupOptions.map((o) => ({ id: o.id, label: o.label }))}
          value={String(specialtyGroup || '')}
        />
      </Form.Group>

      {!!specialtyGroup && (
        <Form.Group controlId="specialty">
          <Form.Label>Specialty</Form.Label>
          <Dropdown
            mandatory={false}
            onItemSelected={(value) => handleDetailsChange('specialties' as keyof T, value || null)}
            options={specialtyOptions}
            value={String(eventDetails?.specialties || '')}
          />
        </Form.Group>
      )}

      <Form.Group controlId="employment-grade">
        <Form.Label>Employment grade</Form.Label>
        <Dropdown
          mandatory={false}
          onItemSelected={(value) => handleDetailsChange('employment_grade' as keyof T, value || null)}
          options={EmploymentGradeOptions.map((o) => ({ id: o.id, label: o.label }))}
          value={String(eventDetails?.employment_grade || '')}
        />
      </Form.Group>

      <Form.Group controlId="year-group">
        <Form.Label>Year group</Form.Label>
        <Dropdown
          mandatory={false}
          onItemSelected={(value) => handleDetailsChange('year_group' as keyof T, value || null)}
          options={YearGroupOptions.map((o) => ({ id: o.id, label: o.label }))}
          value={String(eventDetails?.year_group || '')}
        />
      </Form.Group>
    </EventDetailsSectionLayout>
  );
}
